import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, errMsg } from "@/lib/api";
import { PageHead, card } from "@/components/admin/kit";
import { useFetch } from "@/components/p4/shared";

const STEPS = {
  cronjob: (d) => [
    "Go to cron-job.org and sign up (free), then log in.",
    "Click “Create cronjob”.",
    "Title: OnDose scheduler.",
    "URL: tap “Copy scheduler URL” below and paste it (it contains the secret key — never share it).",
    "Execution schedule: “Every 1 minute”.",
    "Under Advanced: Request method GET, Timeout 30 seconds.",
    "Under Notifications: tick “execution of the cronjob fails”.",
    `Click “Create”. Within 2 minutes after the app is live on ${d}, the status here turns green. Then tick “I've done this”.`,
  ],
  uptimerobot: (d) => [
    "Go to uptimerobot.com and sign up (free), then log in.",
    "Click “+ New monitor”. Monitor type: HTTP(s).",
    `URL: https://${d}/api/health/scheduler (no key — safe to share).`,
    "Monitoring interval: 5 minutes.",
    "Alert contacts: tick your email (add the mobile app for push alerts).",
    "Click “Create monitor”. It shows Down if the scheduler stops for 3+ minutes. Then tick “I've done this”.",
  ],
  clips: () => ["Open Website voice clips in the menu.", "Play every clip shown on the website and check names, medicines and wording.", "Then tick “I've done this”."],
};

const DEPLOY = [
  "Click Publish in the top toolbar of Emergent. Confirm the cost shown, then wait 10–15 minutes until it says Live.",
  "Domain tab → “+ Add your Domain” → type ondose.in → “Link Manually”.",
  "At your domain provider, add the DNS records Emergent shows (A records for @ and a CNAME for www), remove other A records, save.",
  "Back in Emergent click “Check Status” until it says Verified (usually 5–15 minutes).",
  "Secrets tab: check every key is there (Twilio, Clerk, cron key). Change a value → Save → Re-publish changes.",
  "Then come back to this page on ondose.in and finish the cron-job.org and UptimeRobot steps.",
  "After any later change: Republish → Re-publish changes.",
];

function Item({ api, it, domain, reload }) {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const copyCron = async () => { try { const r = await api.get("/admin/golive/cron-url"); await navigator.clipboard.writeText(r.data.url); toast.success("Copied — paste it into cron-job.org"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className={`${card} grid gap-2`} data-testid={`golive-${it.key}`}>
      <p className="flex items-center gap-2 font-bold">{it.ok ? <CheckCircle2 size={18} className="text-green-600" /> : <Circle size={18} className="text-orange-500" />} {it.title}</p>
      <p className="text-sm text-muted-foreground" data-testid={`golive-${it.key}-detail`}>{it.detail}</p>
      {it.key === "logins" && (
        <>
          {it.logins.map((u) => <p key={u.id} className="text-xs">{u.name} · {u.phone} · {u.role}</p>)}
          {it.admins.filter((a) => !a.owner).map((a) => (
            <p key={a.user_id} className="flex items-center gap-2 text-xs">Extra admin: {a.email}
              <Button size="sm" variant="outline" onClick={() => window.confirm(`Remove admin access for ${a.email}?`) && act(() => api.delete(`/admin/golive/admins/${a.user_id}`), "Admin removed")} data-testid={`golive-remove-admin-${a.user_id}`}>Remove</Button></p>
          ))}
          {it.logins.length > 0 && <Button size="sm" variant="outline" className="w-fit" onClick={() => window.confirm("Switch off all sample, test and temporary-password logins?") && act(() => api.post("/admin/golive/switch-off-test-logins"), "Logins switched off")} data-testid="golive-switch-off-logins">Switch off these logins</Button>}
        </>
      )}
      {it.key === "real_patients" && !it.ok && <Button size="sm" variant="outline" className="w-fit" onClick={() => act(() => api.post("/admin/golive/real-patients", { on: false }), "Switched OFF")} data-testid="golive-real-off">Switch OFF</Button>}
      {STEPS[it.key] && (
        <>
          <ol className="ml-5 list-decimal text-sm">{STEPS[it.key](domain).map((s) => <li key={s}>{s}</li>)}</ol>
          <div className="flex flex-wrap gap-2">
            {it.key === "cronjob" && <Button size="sm" variant="outline" onClick={copyCron} data-testid="golive-copy-cron"><Copy size={14} /> Copy scheduler URL</Button>}
            <Button size="sm" variant={it.confirmed ? "default" : "outline"} onClick={() => act(() => api.post("/admin/golive/confirm", { key: it.key, done: !it.confirmed }), "Saved")} data-testid={`golive-confirm-${it.key}`}>{it.confirmed ? "Done ✓ (tap to undo)" : "I've done this"}</Button>
          </div>
        </>
      )}
    </div>
  );
}

export default function GoLive() {
  const api = useApi();
  const [d, reload] = useFetch(api, "/admin/golive");
  const [show, setShow] = useState(false);
  return (
    <div className="grid gap-4" data-testid="golive-page">
      <PageHead title="Before going live" sub={d ? `${d.done} of ${d.total} done. Nothing is deployed until you click Publish.` : "Checking…"}>
        <Button variant="outline" onClick={() => setShow(!show)} data-testid="golive-deploy-steps-btn">How to deploy</Button>
      </PageHead>
      {show && <div className={card} data-testid="golive-deploy-steps"><p className="mb-2 font-bold">When everything is ready — what to click</p><ol className="ml-5 list-decimal text-sm">{DEPLOY.map((s) => <li key={s} className="py-0.5">{s}</li>)}</ol></div>}
      {d?.items.map((it) => <Item key={it.key} api={api} it={it} domain={d.domain} reload={reload} />)}
    </div>
  );
}
